"use client";

import type { Editor } from "@tiptap/react";
import { useCallback, useRef, useState } from "react";
import type { CellSettings, ColumnSettings, RowSettings, TableSettings } from "../modals/UnifiedTableSettingsModal";
import type {
	BreakpointCellConfig,
	BreakpointColumnConfig,
	BreakpointWidthConfig,
	PaddingValues,
	ResponsiveCellStyles,
	ResponsiveColumnStyles,
	ResponsiveTableWidths
} from "../types";
import {
	extractNumericValue,
	parsePaddingString,
	parseStyleString
} from "../utils/styleUtils";
import {
	DEFAULT_BREAKPOINT_WIDTH,
	createDefaultCellConfig,
	createDefaultColumnConfig,
	createDefaultRowConfig,
	getCurrentColumnIndex,
	getCurrentRowIndex,
	getTotalColumns,
	getTotalRows,
	readColumnSettingsFromDOM
} from "../utils/tableUtils";

// Helper to convert RGB color to hex format
function rgbToHex(color: string): string {
	if (!color) return "";
	// If already hex, return as is
	if (color.startsWith("#")) return color;

	// Parse rgb(r, g, b) or rgba(r, g, b, a) format
	const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
	if (rgbMatch) {
		const r = parseInt(rgbMatch[1], 10);
		const g = parseInt(rgbMatch[2], 10);
		const b = parseInt(rgbMatch[3], 10);
		return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
	}

	// Return original if not recognized
	return color;
}

// Helper to parse CSS shorthand border-color/border-width
// Handles: "value" | "top right" | "top right bottom" | "top right bottom left"
function parseShorthandBorderProperty(value: string): { top: string; right: string; bottom: string; left: string } | null {
	if (!value) return null;

	// For border-color, split by rgb/rgba boundaries
	// e.g., "rgb(159, 35, 35) rgb(0, 0, 0) rgb(0, 0, 0)"
	const rgbMatches = value.match(/rgba?\([^)]+\)/g);
	if (rgbMatches) {
		const values = rgbMatches;
		if (values.length === 1) {
			return { top: values[0], right: values[0], bottom: values[0], left: values[0] };
		} else if (values.length === 2) {
			return { top: values[0], right: values[1], bottom: values[0], left: values[1] };
		} else if (values.length === 3) {
			return { top: values[0], right: values[1], bottom: values[2], left: values[1] };
		} else if (values.length >= 4) {
			return { top: values[0], right: values[1], bottom: values[2], left: values[3] };
		}
	}

	// For border-width or simple values, split by whitespace
	// e.g., "1px 0px 0px" or "1px 2px 3px 4px"
	const parts = value.trim().split(/\s+/);
	if (parts.length === 1) {
		return { top: parts[0], right: parts[0], bottom: parts[0], left: parts[0] };
	} else if (parts.length === 2) {
		return { top: parts[0], right: parts[1], bottom: parts[0], left: parts[1] };
	} else if (parts.length === 3) {
		return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[1] };
	} else if (parts.length >= 4) {
		return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] };
	}

	return null;
}

// Helper to expand compact cell settings format back to full format
function expandCompactCellSettings(compact: Record<string, Record<string, unknown>>): ResponsiveCellStyles {
	const parseBpConfig = (c: Record<string, unknown>): BreakpointCellConfig => {
		// Parse compact padding "t,r,b,l" format
		let padding: PaddingValues = { top: "8", right: "12", bottom: "8", left: "12" };
		if (c.p && typeof c.p === "string") {
			const [t, r, b, l] = c.p.split(",");
			padding = { top: t || "8", right: r || "12", bottom: b || "8", left: l || "12" };
		}
		// Parse compact border format
		let border: BreakpointCellConfig["border"] = undefined;
		if (c.bd && typeof c.bd === "string") {
			border = { top: { color: "", width: "" }, right: { color: "", width: "" }, bottom: { color: "", width: "" }, left: { color: "", width: "" } };
			c.bd.split(";").forEach((part: string) => {
				const [side, color, width] = part.split(":");
				if (side === "t" && border?.top) { border.top = { color: color || "", width: width || "" }; }
				if (side === "r" && border?.right) { border.right = { color: color || "", width: width || "" }; }
				if (side === "b" && border?.bottom) { border.bottom = { color: color || "", width: width || "" }; }
				if (side === "l" && border?.left) { border.left = { color: color || "", width: width || "" }; }
			});
		}

		return {
			backgroundColor: (c.bg as string) || "",
			textColor: (c.c as string) || "",
			textAlign: ((c.ta as string) || "left") as "left" | "center" | "right",
			verticalAlign: ((c.va as string) || "middle") as "top" | "middle" | "bottom",
			fontSize: (c.fs as string) || "",
			fontWeight: (c.fw as string) || "",
			padding,
			border,
		};
	};

	return {
		xs: parseBpConfig(compact.xs || {}),
		sm: parseBpConfig(compact.sm || {}),
		md: parseBpConfig(compact.md || {}),
		lg: parseBpConfig(compact.lg || {}),
		xl: parseBpConfig(compact.xl || {}),
	};
}

export interface UseTableSettingsStateReturn {
	// Table creation state
	tableRows: number;
	setTableRows: (v: number) => void;
	tableCols: number;
	setTableCols: (v: number) => void;
	tableWithHeader: boolean;
	setTableWithHeader: (v: boolean) => void;
	tableBorderColor: string;
	setTableBorderColor: (v: string) => void;
	tableBorderWidth: number;
	setTableBorderWidth: (v: number) => void;
	showTableModal: boolean;
	setShowTableModal: (v: boolean) => void;

	// Unified Table Settings Modal
	showUnifiedTableSettings: boolean;
	setShowUnifiedTableSettings: (v: boolean) => void;
	totalColumns: number;
	setTotalColumns: (v: number) => void;
	totalRows: number;
	setTotalRows: (v: number) => void;
	currentTableId: string | null;

	// Table Settings
	tableSettings: TableSettings;
	setTableSettings: React.Dispatch<React.SetStateAction<TableSettings>>;

	// Column Settings
	columnSettings: ColumnSettings;
	setColumnSettings: React.Dispatch<React.SetStateAction<ColumnSettings>>;
	allColumnSettings: Record<number, ColumnSettings>;
	setAllColumnSettings: React.Dispatch<React.SetStateAction<Record<number, ColumnSettings>>>;

	// Row Settings
	rowSettings: RowSettings;
	setRowSettings: React.Dispatch<React.SetStateAction<RowSettings>>;
	allRowSettings: Record<number, RowSettings>;
	setAllRowSettings: React.Dispatch<React.SetStateAction<Record<number, RowSettings>>>;

	// Cell Settings
	cellSettings: CellSettings;
	setCellSettings: React.Dispatch<React.SetStateAction<CellSettings>>;

	// Selected cell indices (captured when modal opens)
	selectedCellIndices: number[];

	// Internal change ref
	isInternalChange: React.MutableRefObject<boolean>;

	// Flag to prevent auto-reopen after closing via "Apply" button
	shouldPreventAutoReopen: React.MutableRefObject<boolean>;

	// Methods
	handleColumnSelect: (colIndex: number) => void;
	handleRowSelect: (rowIndex: number) => void;
	openUnifiedTableSettings: (editor: Editor | null) => void;
	refreshCellSettings: (editor: Editor | null) => void;
	closeUnifiedTableSettingsWithoutReopen: () => void;
}

export const useTableSettingsState = (): UseTableSettingsStateReturn => {
	const isInternalChange = useRef(false);

	// Flag to prevent auto-reopen after closing via "Apply" button
	const shouldPreventAutoReopen = useRef(false);

	// Table creation state
	const [tableRows, setTableRows] = useState(3);
	const [tableCols, setTableCols] = useState(3);
	const [tableWithHeader, setTableWithHeader] = useState(true);
	const [tableBorderColor, setTableBorderColor] = useState("#000000");
	const [tableBorderWidth, setTableBorderWidth] = useState(1);
	const [showTableModal, setShowTableModal] = useState(false);

	// Unified Table Settings Modal
	const [showUnifiedTableSettings, setShowUnifiedTableSettings] = useState(false);
	const [totalColumns, setTotalColumns] = useState(3);
	const [totalRows, setTotalRows] = useState(3);
	const [currentTableId, setCurrentTableId] = useState<string | null>(null);

	// Store selected cell indices when modal opens (to preserve selection across modal interactions)
	const [selectedCellIndices, setSelectedCellIndices] = useState<number[]>([]);

	// Default configs
	const defaultBreakpointWidth: BreakpointWidthConfig = { ...DEFAULT_BREAKPOINT_WIDTH };
	const defaultColumnConfig = createDefaultColumnConfig();
	const defaultRowConfig = createDefaultRowConfig();
	const defaultCellConfig = createDefaultCellConfig();

	// Table Settings State
	const [tableSettings, setTableSettings] = useState<TableSettings>({
		tableWidths: {
			xs: { ...defaultBreakpointWidth },
			sm: { ...defaultBreakpointWidth },
			md: { ...defaultBreakpointWidth },
			lg: { ...defaultBreakpointWidth },
			xl: { ...defaultBreakpointWidth },
		},
		tableBorderColor: "#000000",
		tableBorderWidth: 1,
		tableBorderRadius: 0,
		tableRowGap: 0,
	});

	// Column Settings State
	const [columnSettings, setColumnSettings] = useState<ColumnSettings>({
		columnIndex: 0,
		styles: {
			xs: { ...defaultColumnConfig },
			sm: { ...defaultColumnConfig },
			md: { ...defaultColumnConfig },
			lg: { ...defaultColumnConfig },
			xl: { ...defaultColumnConfig },
		},
	});

	// Store all column settings by index for persistence
	const [allColumnSettings, setAllColumnSettings] = useState<Record<number, ColumnSettings>>({});

	// Row Settings State
	const [rowSettings, setRowSettings] = useState<RowSettings>({
		rowIndex: 0,
		styles: {
			xs: { ...defaultRowConfig },
			sm: { ...defaultRowConfig },
			md: { ...defaultRowConfig },
			lg: { ...defaultRowConfig },
			xl: { ...defaultRowConfig },
		},
	});

	// Store all row settings by index for persistence
	const [allRowSettings, setAllRowSettings] = useState<Record<number, RowSettings>>({});

	// Cell Settings State
	const [cellSettings, setCellSettings] = useState<CellSettings>({
		styles: {
			xs: { ...defaultCellConfig },
			sm: { ...defaultCellConfig },
			md: { ...defaultCellConfig },
			lg: { ...defaultCellConfig },
			xl: { ...defaultCellConfig },
		},
	});

	// Handle column selection in the modal
	const handleColumnSelect = useCallback((colIndex: number) => {
		if (allColumnSettings[colIndex]) {
			setColumnSettings({ ...allColumnSettings[colIndex], columnIndex: colIndex });
			return;
		}
		const newSettings = readColumnSettingsFromDOM(null, colIndex);
		setColumnSettings(newSettings);
	}, [allColumnSettings]);

	// Handle row selection in the modal
	const handleRowSelect = useCallback((rowIndex: number) => {
		if (allRowSettings[rowIndex]) {
			setRowSettings({ ...allRowSettings[rowIndex], rowIndex: rowIndex });
			return;
		}
		// Default row settings
		setRowSettings({
			rowIndex: rowIndex,
			styles: {
				xs: { ...defaultRowConfig },
				sm: { ...defaultRowConfig },
				md: { ...defaultRowConfig },
				lg: { ...defaultRowConfig },
				xl: { ...defaultRowConfig },
			},
		});
	}, [allRowSettings, defaultRowConfig]);

	// Refresh cell settings from currently selected cell
	const refreshCellSettings = useCallback((editor: Editor | null) => {
		if (!editor) return;

		// Find the CURRENT table (using currentTableId state or detecting from selection)
		const pmEditor = document.querySelector(".ProseMirror");
		if (!pmEditor) {
			console.log("No ProseMirror editor found");
			return;
		}

		// Try to find the current table - prefer table with selected cells
		let pmTable: Element | null = null;
		const allTables = pmEditor.querySelectorAll("table");

		// First, look for table with selected cells
		allTables.forEach((table) => {
			if (table.querySelector(".selectedCell, .ProseMirror-selectednode")) {
				pmTable = table;
			}
		});

		// If no table with selected cells, try to find table containing cursor
		if (!pmTable) {
			const selection = window.getSelection();
			if (selection && selection.focusNode) {
				let node: Node | null = selection.focusNode;
				while (node && node !== pmEditor) {
					if (node.nodeName === "TABLE") {
						pmTable = node as Element;
						break;
					}
					node = node.parentNode;
				}
			}
		}

		// Fallback to first table
		if (!pmTable) {
			pmTable = allTables[0] || null;
		}

		if (!pmTable) {
			console.log("No table found in editor");
			return;
		}

		// Check for selected cells (cells with selectedCell class)
		const selectedCell = pmTable.querySelector(".selectedCell");
		const currentTableIdFromDOM = pmTable.getAttribute("data-table-id") || "";

		// Update currentTableId if it changed (user clicked on a different table)
		if (currentTableIdFromDOM && currentTableIdFromDOM !== currentTableId) {
			console.log("refreshCellSettings - Table changed from", currentTableId, "to", currentTableIdFromDOM);
			setCurrentTableId(currentTableIdFromDOM);
		}

		// Try from editor state - the selection should still be valid even when modal is open
		const { selection } = editor.state;
		const { $from } = selection;

		console.log("Selection position:", $from.pos, "depth:", $from.depth);
		let cellNode = null;
		let cellAttrs: Record<string, unknown> = {};
		let detectedCellIndex = -1;

		// First try from editor's ProseMirror state - and also find the cell index
		for (let depth = $from.depth; depth > 0; depth--) {
			const node = $from.node(depth);
			if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
				cellNode = node;
				cellAttrs = node.attrs || {};

				// Find the cell index within the current table
				let cellCounter = 0;
				let foundTableId: string | null = null;
				const selPos = $from.pos;

				editor.state.doc.descendants((n, pos) => {
					if (detectedCellIndex >= 0) return false; // Stop if found
					if (n.type.name === "table") {
						foundTableId = n.attrs["data-table-id"] || null;
						cellCounter = 0;
						return foundTableId === currentTableIdFromDOM || !currentTableIdFromDOM;
					}
					if ((n.type.name === "tableCell" || n.type.name === "tableHeader") &&
						(foundTableId === currentTableIdFromDOM || !currentTableIdFromDOM)) {
						if (selPos >= pos && selPos < pos + n.nodeSize) {
							detectedCellIndex = cellCounter;
							console.log("refreshCellSettings - Detected cell index:", detectedCellIndex);
						}
						cellCounter++;
					}
					return true;
				});

				break;
			}
		}

		// NOTE: Do NOT update selectedCellIndices here!
		// selectedCellIndices should only be set by openUnifiedTableSettings when modal opens
		// to preserve multi-cell selection. refreshCellSettings is only for loading style settings.

		// Try to read from table-level metadata first (most reliable)
		const allCells = pmTable.querySelectorAll("td, th");
		let domCellElement: HTMLElement | null = null;
		if (detectedCellIndex >= 0 && allCells[detectedCellIndex]) {
			domCellElement = allCells[detectedCellIndex] as HTMLElement;
		}

		// Try reading from table's data-cell-styles metadata (supports both single and double quotes)
		let tableCellStylesAttr = pmTable.getAttribute("data-cell-styles");
		if (!tableCellStylesAttr) {
			// Fallback: try reading directly from outerHTML if getAttribute didn't work
			const tableHTML = pmTable.outerHTML;
			const singleQuoteMatch = tableHTML.match(/data-cell-styles='([^']+)'/);
			const doubleQuoteMatch = tableHTML.match(/data-cell-styles="([^"]+)"/);
			tableCellStylesAttr = (singleQuoteMatch && singleQuoteMatch[1]) || (doubleQuoteMatch && doubleQuoteMatch[1]) || null;
		}
		if (tableCellStylesAttr && domCellElement) {
			const cellClass = domCellElement.getAttribute("data-cell-class");
			if (cellClass) {
				try {
					const allCellStyles = JSON.parse(tableCellStylesAttr);
					const cellData = allCellStyles[cellClass];
					if (cellData) {
						// Handle both old format (styles) and new compact format (s)
						if (cellData.styles) {
							setCellSettings(cellData);
							console.log("Loaded cell settings from table metadata (full format)");
							return;
						} else if (cellData.s) {
							// Expand compact format
							const expanded = expandCompactCellSettings(cellData.s);
							setCellSettings({ styles: expanded });
							console.log("Loaded cell settings from table metadata (compact format)");
							return;
						}
					}
				} catch (e) {
					console.error("Failed to parse table cell styles metadata:", e);
					// Fall through
				}
			}
		}

		// Fallback: Try reading from DOM element's data-cell-settings attribute
		if (domCellElement) {
			const domSavedSettings = domCellElement.getAttribute("data-cell-settings");
			if (domSavedSettings) {
				try {
					const parsed = JSON.parse(domSavedSettings);
					// Handle both old format (styles) and new compact format (s)
					if (parsed.styles) {
						setCellSettings(parsed);
						console.log("Loaded cell settings from DOM saved data (full format)");
						return;
					} else if (parsed.s) {
						// Expand compact format
						const expanded = expandCompactCellSettings(parsed.s);
						setCellSettings({ styles: expanded });
						console.log("Loaded cell settings from DOM compact format");
						return;
					}
				} catch (e) {
					console.error("Failed to parse DOM cell settings:", e);
					// Fall through to read from inline styles
				}
			}
		}

		// If we found a cell in the editor state, use its attributes
		if (cellNode) {
			const styleStr = (cellAttrs.style as string) || "";
			const styles = parseStyleString(styleStr);

			const savedCellSettings = cellAttrs["data-cell-settings"] as string | undefined;
			if (savedCellSettings) {
				try {
					const parsed = JSON.parse(savedCellSettings);
					// Handle both old format (styles) and new compact format (s)
					if (parsed.styles) {
						setCellSettings(parsed);
						console.log("Loaded cell settings from PM node saved data");
						return;
					} else if (parsed.s) {
						// Expand compact format
						const expanded = expandCompactCellSettings(parsed.s);
						setCellSettings({ styles: expanded });
						console.log("Loaded cell settings from PM node compact format");
						return;
					}
				} catch {
					// Fall through to read from inline styles
				}
			}

			// Read from inline styles/attributes
			// Inline styles represent the XS (base) breakpoint only
			// Other breakpoints should be empty unless explicitly set

			// Parse shorthand border properties if they exist
			const borderColorShorthand = parseShorthandBorderProperty(styles["border-color"] || "");
			const borderWidthShorthand = parseShorthandBorderProperty(styles["border-width"] || "");

			// Helper to get border side config - if width is 0, treat as not set (empty color)
			const getBorderSide = (side: "top" | "right" | "bottom" | "left") => {
				const width = extractNumericValue(styles[`border-${side}-width`] || borderWidthShorthand?.[side]) || "1";
				const color = rgbToHex(styles[`border-${side}-color`] || borderColorShorthand?.[side] || "");

				// If width is 0, clear the color (border not set)
				if (width === "0") {
					return { color: "", width: "1" };
				}

				return { color, width };
			};

			const cellConfigFromStyles: BreakpointCellConfig = {
				backgroundColor: rgbToHex((cellAttrs.backgroundColor as string) || styles["background-color"] || ""),
				textColor: rgbToHex((cellAttrs.textColor as string) || styles.color || ""),
				textAlign: ((cellAttrs.textAlign as string) || styles["text-align"] || "left") as "left" | "center" | "right",
				verticalAlign: ((cellAttrs.verticalAlign as string) || styles["vertical-align"] || "middle") as "top" | "middle" | "bottom",
				padding: parsePaddingString(styles.padding || (cellAttrs.padding as string)),
				fontSize: extractNumericValue((cellAttrs.fontSize as string) || styles["font-size"]),
				fontWeight: (cellAttrs.fontWeight as string) || styles["font-weight"] || "",
				// Read individual border properties, with proper shorthand parsing
				border: {
					top: getBorderSide("top"),
					right: getBorderSide("right"),
					bottom: getBorderSide("bottom"),
					left: getBorderSide("left"),
				},
			};

			// IMPORTANT: Only set XS with the inline styles
			// Other breakpoints start with default empty values
			setCellSettings({
				styles: {
					xs: { ...cellConfigFromStyles },
					sm: { ...defaultCellConfig },  // Empty/default
					md: { ...defaultCellConfig },  // Empty/default
					lg: { ...defaultCellConfig },  // Empty/default
					xl: { ...defaultCellConfig },  // Empty/default
				},
			});
			console.log("Loaded cell settings from inline styles (XS only, other breakpoints empty)");
		} else if (selectedCell) {
			// Fallback: Read from DOM element directly
			const cellEl = selectedCell as HTMLElement;
			const savedSettings = cellEl.getAttribute("data-cell-settings");

			if (savedSettings) {
				try {
					const parsed = JSON.parse(savedSettings);
					if (parsed.styles) {
						setCellSettings(parsed);
						console.log("Loaded cell settings from DOM data attribute");
						return;
					} else if (parsed.s) {
						const expanded = expandCompactCellSettings(parsed.s);
						setCellSettings({ styles: expanded });
						console.log("Loaded cell settings from compact DOM format");
						return;
					}
				} catch {
					// Fall through
				}
			}

			// Read from inline styles (individual properties)
			// Use rgbToHex to convert browser's rgb() format back to hex
			const cellConfigFromDOM: BreakpointCellConfig = {
				backgroundColor: rgbToHex(cellEl.style.backgroundColor || ""),
				textColor: rgbToHex(cellEl.style.color || ""),
				textAlign: (cellEl.style.textAlign || "left") as "left" | "center" | "right",
				verticalAlign: (cellEl.style.verticalAlign || "middle") as "top" | "middle" | "bottom",
				padding: parsePaddingString(cellEl.style.padding),
				fontSize: extractNumericValue(cellEl.style.fontSize),
				fontWeight: cellEl.style.fontWeight || "",
				// Read individual border properties (not shorthand)
				border: {
					top: {
						color: rgbToHex(cellEl.style.borderTopColor || cellEl.style.borderColor || ""),
						width: extractNumericValue(cellEl.style.borderTopWidth || cellEl.style.borderWidth) || "1",
					},
					right: {
						color: rgbToHex(cellEl.style.borderRightColor || cellEl.style.borderColor || ""),
						width: extractNumericValue(cellEl.style.borderRightWidth || cellEl.style.borderWidth) || "1",
					},
					bottom: {
						color: rgbToHex(cellEl.style.borderBottomColor || cellEl.style.borderColor || ""),
						width: extractNumericValue(cellEl.style.borderBottomWidth || cellEl.style.borderWidth) || "1",
					},
					left: {
						color: rgbToHex(cellEl.style.borderLeftColor || cellEl.style.borderColor || ""),
						width: extractNumericValue(cellEl.style.borderLeftWidth || cellEl.style.borderWidth) || "1",
					},
				},
			};

			// IMPORTANT: Only set XS with the DOM inline styles
			// Other breakpoints start with default empty values
			setCellSettings({
				styles: {
					xs: { ...cellConfigFromDOM },
					sm: { ...defaultCellConfig },  // Empty/default
					md: { ...defaultCellConfig },  // Empty/default
					lg: { ...defaultCellConfig },  // Empty/default
					xl: { ...defaultCellConfig },  // Empty/default
				},
			});
			console.log("Loaded cell settings from DOM computed styles (XS only, other breakpoints empty)");
		} else {
			// No cell selected - reset to defaults
			console.log("No cell selected, resetting to defaults");
			setCellSettings({
				styles: {
					xs: { ...defaultCellConfig },
					sm: { ...defaultCellConfig },
					md: { ...defaultCellConfig },
					lg: { ...defaultCellConfig },
					xl: { ...defaultCellConfig },
				},
			});
		}
	}, [defaultCellConfig]);

	// Open unified settings modal
	const openUnifiedTableSettings = useCallback((editor: Editor | null) => {
		if (!editor) return;

		// IMPORTANT: Capture selection state BEFORE focusing (focus might clear multi-cell selection)
		console.log("openUnifiedTableSettings - Selection before focus:", editor.state.selection.constructor.name);

		const cols = getTotalColumns(editor);
		const rows = getTotalRows(editor);
		setTotalColumns(cols);
		setTotalRows(rows);

		const currentColIndex = getCurrentColumnIndex(editor);
		const currentRowIndex = getCurrentRowIndex(editor);

		// Read current table settings from TipTap's table attributes
		const tableAttrs = editor.getAttributes("table");

		// Determine the CORRECT table ID - prefer DOM-based detection over editor.getAttributes
		// This is more reliable when there are multiple tables
		let tableId = tableAttrs["data-table-id"] as string | null;

		// Try to detect the correct table from DOM (more reliable for multi-table scenarios)
		const editorEl = document.querySelector(".ProseMirror");
		if (editorEl) {
			const allTablesInEditor = editorEl.querySelectorAll("table");

			// First, check if any table has selected cells
			allTablesInEditor.forEach((table) => {
				const selectedCells = table.querySelectorAll(".selectedCell, .ProseMirror-selectednode");
				if (selectedCells.length > 0) {
					const detectedId = table.getAttribute("data-table-id");
					if (detectedId) {
						tableId = detectedId;
					}
				}
			});

			// If no table with selected cells, try to find table containing the cursor
			if (!tableId) {
				const selection = window.getSelection();
				if (selection && selection.focusNode) {
					let node: Node | null = selection.focusNode;
					while (node && node !== editorEl) {
						if (node.nodeName === "TABLE") {
							const detectedId = (node as HTMLElement).getAttribute("data-table-id");
							if (detectedId) {
								tableId = detectedId;
							}
							break;
						}
						node = node.parentNode;
					}
				}
			}
		}

		// Capture and store the current table's ID for use in apply functions
		setCurrentTableId(tableId);

		// editor.getAttributes("table") resolves from the cursor, which can point at
		// a different table than the one detected above (multi-cell selections, or a
		// cursor that never entered the table). Re-read the settings from the table
		// that was actually resolved so the panel shows that table's values.
		let resolvedTableAttrs = tableAttrs;
		if (tableId) {
			editor.state.doc.descendants((node) => {
				if (node.type.name !== "table") return true;
				if (node.attrs["data-table-id"] === tableId) resolvedTableAttrs = node.attrs;
				return false;
			});
		}

		// Helper to parse breakpoint width from "type:value:alignment:scroll:gap" format
		const parseBreakpointWidth = (str: string | undefined): BreakpointWidthConfig => {
			const defaultConfig: BreakpointWidthConfig = { type: "percent", value: "100", alignment: "left", horizontalScroll: false };
			if (!str) return defaultConfig;
			const parts = str.split(":");
			const type = parts[0] as string;
			return {
				type: (type === "auto" || !type ? "percent" : type) as "percent" | "pixel",
				value: parts[1] || "",
				alignment: (parts[2] as "left" | "center" | "right") || "left",
				horizontalScroll: parts[3] === "1",
				rowGap: parts[4] ? parseInt(parts[4]) : undefined,
			};
		};

		// Read table settings from individual attributes (like cell styles)
		const tableWidths: ResponsiveTableWidths = {
			xs: parseBreakpointWidth(resolvedTableAttrs["data-width-xs"] as string),
			sm: parseBreakpointWidth(resolvedTableAttrs["data-width-sm"] as string),
			md: parseBreakpointWidth(resolvedTableAttrs["data-width-md"] as string),
			lg: parseBreakpointWidth(resolvedTableAttrs["data-width-lg"] as string),
			xl: parseBreakpointWidth(resolvedTableAttrs["data-width-xl"] as string),
		};

		// Border settings live on the borderColor/borderWidth/borderRadius attrs,
		// which CustomTable parses back out of data-border-*.
		const borderColor = rgbToHex((resolvedTableAttrs.borderColor as string) || "") || "#000000";
		const parsedBorderWidth = parseFloat(String(resolvedTableAttrs.borderWidth));
		const borderWidth = isNaN(parsedBorderWidth) ? 1 : parsedBorderWidth;
		const parsedBorderRadius = parseFloat(String(resolvedTableAttrs.borderRadius));
		const borderRadius = isNaN(parsedBorderRadius) ? 0 : parsedBorderRadius;

		setTableSettings({
			tableWidths,
			tableBorderColor: borderColor,
			tableBorderWidth: borderWidth,
			tableBorderRadius: borderRadius,
			tableRowGap: tableWidths.xs?.rowGap || 0,
		});

		// Read saved row settings from the table's data attribute (source of truth)
		const savedAllRowSettingsAttr = resolvedTableAttrs["data-all-row-settings"];
		let loadedRowSettings: Record<number, RowSettings> = {};
		if (savedAllRowSettingsAttr) {
			try {
				loadedRowSettings = JSON.parse(savedAllRowSettingsAttr);
				setAllRowSettings(loadedRowSettings);
			} catch {
				setAllRowSettings({});
			}
		} else {
			// New table - clear any stale row settings
			setAllRowSettings({});
		}

		// Helper to parse column width from "type:value" format
		const parseColWidth = (str: string | undefined): BreakpointColumnConfig => {
			const defaultConfig: BreakpointColumnConfig = {
				widthType: "percent",
				widthValue: "33",
				textAlign: "left",
				verticalAlign: "middle",
				padding: { top: "8", right: "8", bottom: "8", left: "8" },
			};
			if (!str) return defaultConfig;
			const parts = str.split(":");
			const type = parts[0] as string;
			return {
				...defaultConfig,
				widthType: (type === "auto" || !type ? "percent" : type) as "percent" | "pixel",
				widthValue: parts[1] || "",
			};
		};

		// Read ALL column settings from individual cell attributes (like cell styles)
		// IMPORTANT: Only read from the CURRENT table (matching tableId), not all tables
		let loadedColumnSettings: Record<number, ColumnSettings> = {};
		let foundCurrentTable = false;
		let rowIdxInCurrentTable = 0;

		editor.state.doc.descendants((node) => {
			// Check if we've reached a table node
			if (node.type.name === "table") {
				// Check if this is the current table by matching data-table-id
				const nodeTableId = node.attrs["data-table-id"];
				foundCurrentTable = nodeTableId === tableId;
				rowIdxInCurrentTable = 0; // Reset row counter for each table
				return true; // Continue traversing children
			}

			// Only process rows if we're in the current table
			if (node.type.name === "tableRow" && foundCurrentTable) {
				if (rowIdxInCurrentTable === 0) {
					// Read from first row of CURRENT TABLE where column settings are stored
					let cellIdx = 0;
					node.forEach((cell) => {
						const attrs = cell.attrs;
						// Check if any breakpoint has width data
						const hasWidthData = ["xs", "sm", "md", "lg", "xl"].some(
							bp => attrs[`data-col-width-${bp}`]
						);

						if (hasWidthData) {
							const styles: ResponsiveColumnStyles = {
								xs: { ...parseColWidth(attrs["data-col-width-xs"]) },
								sm: { ...parseColWidth(attrs["data-col-width-sm"]) },
								md: { ...parseColWidth(attrs["data-col-width-md"]) },
								lg: { ...parseColWidth(attrs["data-col-width-lg"]) },
								xl: { ...parseColWidth(attrs["data-col-width-xl"]) },
							};
							loadedColumnSettings[cellIdx] = {
								columnIndex: cellIdx,
								styles,
							};
						}
						cellIdx++;
					});
				}
				rowIdxInCurrentTable++;
				return false; // Don't traverse into row children (we already processed cells)
			}

			return true; // Continue traversing
		});

		// ALWAYS update allColumnSettings state with loaded data for the CURRENT table
		// This clears any stale data from a previously opened table
		setAllColumnSettings(loadedColumnSettings);

		// Read current column settings from loaded data or use defaults
		if (loadedColumnSettings[currentColIndex]) {
			setColumnSettings({ ...loadedColumnSettings[currentColIndex], columnIndex: currentColIndex });
		} else {
			// Use default column settings for this column (don't use stale data from other tables)
			setColumnSettings({
				columnIndex: currentColIndex,
				styles: {
					xs: { ...defaultColumnConfig },
					sm: { ...defaultColumnConfig },
					md: { ...defaultColumnConfig },
					lg: { ...defaultColumnConfig },
					xl: { ...defaultColumnConfig },
				},
			});
		}

		// Read current row settings from loaded data or use defaults
		if (loadedRowSettings[currentRowIndex]) {
			setRowSettings({ ...loadedRowSettings[currentRowIndex], rowIndex: currentRowIndex });
		} else {
			setRowSettings({
				rowIndex: currentRowIndex,
				styles: {
					xs: { ...defaultRowConfig },
					sm: { ...defaultRowConfig },
					md: { ...defaultRowConfig },
					lg: { ...defaultRowConfig },
					xl: { ...defaultRowConfig },
				},
			});
		}

		// Reset cell settings to defaults when opening a new table
		// (Cell settings will be refreshed when user clicks on a cell in the Cell Styles tab)
		setCellSettings({
			styles: {
				xs: { ...defaultCellConfig },
				sm: { ...defaultCellConfig },
				md: { ...defaultCellConfig },
				lg: { ...defaultCellConfig },
				xl: { ...defaultCellConfig },
			},
		});

		// Capture selected cells BEFORE modal opens
		// STRATEGY: Use DOM to find cells with .selectedCell class (most reliable for multi-select)
		// Then map those to cell indices within the current table

		const capturedIndices: number[] = [];

		// First, find the current table in the DOM
		const pmEditor = document.querySelector(".ProseMirror");
		if (!pmEditor) {
			console.log("No ProseMirror editor found");
			setSelectedCellIndices([]);
			return;
		}

		// Find ALL tables and check which one has selected cells
		const allTables = pmEditor.querySelectorAll("table");
		let targetTable: Element | null = null;
		let detectedTableId: string | null = null;

		allTables.forEach((table) => {
			const selectedCells = table.querySelectorAll(".selectedCell");
			if (selectedCells.length > 0) {
				targetTable = table;
				detectedTableId = table.getAttribute("data-table-id");
			}
		});

		// If no table with selected cells found, use the provided tableId
		if (!targetTable && tableId) {
			targetTable = pmEditor.querySelector(`table[data-table-id="${tableId}"]`);
			detectedTableId = tableId;
		}

		// Fallback to first table
		if (!targetTable) {
			targetTable = allTables[0] || null;
			detectedTableId = targetTable?.getAttribute("data-table-id") || null;
		}

		const currentTableId = detectedTableId || tableId;
		console.log("Target table ID:", currentTableId, "Found table:", !!targetTable);

		if (targetTable) {
			const allCells = targetTable.querySelectorAll("td, th");
			const selectedCells = targetTable.querySelectorAll(".selectedCell, .ProseMirror-selectednode");

			console.log("Total cells in table:", allCells.length, "Selected cells (.selectedCell | .ProseMirror-selectednode):", selectedCells.length);

			// PRIMARY: Try to use selectedCell or ProseMirror-selectednode class from DOM
			if (selectedCells.length > 0) {
				selectedCells.forEach((selectedCell) => {
					allCells.forEach((cell, idx) => {
						if (cell === selectedCell) {
							capturedIndices.push(idx);
							console.log("Method 1: Found selected cell at index:", idx);
						}
					});
				});
				console.log("Method 1: Captured from DOM classes:", capturedIndices, "Total selected:", selectedCells.length);
			} else {
				console.log("Method 1: No cells with selection classes found in DOM");
			}

			// FALLBACK: If no cells from DOM, try editor's selection state ranges
			if (capturedIndices.length === 0) {
				const { selection } = editor.state;
				const { ranges } = selection;

				console.log("Method 2: Selection ranges:", ranges.length, "Selection constructor:", selection.constructor.name);
				console.log("Method 2: Selection details - from:", selection.$from.pos, "to:", selection.$to.pos);
				if (ranges.length > 0) {
					ranges.forEach((range, idx) => {
						console.log(`Method 2: Range ${idx} - from:`, range.$from.pos, "to:", range.$to.pos);
					});
				}

				// For CellSelection (multi-cell selection), iterate through all cells in the selection range
				if (ranges && ranges.length > 0) {
					let cellCount = 0;
					editor.state.doc.descendants((node, pos) => {
						if (node.type.name === "table") {
							const tableIdAttr = node.attrs["data-table-id"];
							if (tableIdAttr === currentTableId) {
								cellCount = 0; // Reset counter for this table
								return true; // Continue into this table
							} else {
								return false; // Skip other tables
							}
						}

						if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
							// Check if this cell position is within any selection range
							// A cell is selected if its START position is within the range
							for (const range of ranges) {
								const rangeStart = range.$from.pos;
								const rangeEnd = range.$to.pos;

								// Check if cell start position is within or at the boundary of the selection range
								if (pos >= rangeStart && pos <= rangeEnd) {
									if (!capturedIndices.includes(cellCount)) {
										capturedIndices.push(cellCount);
										console.log("Method 2: Captured cell index from range:", cellCount, "pos:", pos, "range:", rangeStart, "-", rangeEnd);
									}
									break;
								}
							}
							cellCount++;
						}
						return true;
					});
				}
			}

			// Final fallback: If still no cells, use cursor position (single cell selection)
			if (capturedIndices.length === 0) {
				// No .selectedCell class - try to find cell at cursor using editor state
				const { selection } = editor.state;
				const { $from } = selection;

				// Find the cell at cursor position
				for (let depth = $from.depth; depth > 0; depth--) {
					const node = $from.node(depth);
					if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
						let cellIndex = 0;
						let found = false;
						let inTable = false;
						editor.state.doc.descendants((n, pos) => {
							if (found) return false;
							if (n.type.name === "table") {
								cellIndex = 0;
								inTable = n.attrs["data-table-id"] === currentTableId;
								return inTable;
							}
							if (inTable && (n.type.name === "tableCell" || n.type.name === "tableHeader")) {
								if (pos <= $from.pos && $from.pos < pos + n.nodeSize) {
									capturedIndices.push(cellIndex);
									found = true;
								}
								cellIndex++;
							}
						});
						break;
					}
				}
			}
		}

		console.log("Captured cell indices:", capturedIndices, "for table:", currentTableId);
		setSelectedCellIndices(capturedIndices);

		// Read current cell settings from selected cell
		const { selection: cellSelection } = editor.state;
		const { $from: cellFrom } = cellSelection;
		let cellNode = null;
		for (let depth = cellFrom.depth; depth > 0; depth--) {
			const node = cellFrom.node(depth);
			if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
				cellNode = node;
				break;
			}
		}

		if (cellNode) {
			const attrs = cellNode.attrs || {};
			const styleStr = attrs.style || "";
			const styles = parseStyleString(styleStr);

			const savedCellSettings = attrs["data-cell-settings"];
			if (savedCellSettings) {
				try {
					const parsed = JSON.parse(savedCellSettings);
					if (parsed.styles) {
						setCellSettings(parsed);
					} else if (parsed.s) {
						const expanded = expandCompactCellSettings(parsed.s);
						setCellSettings({ styles: expanded });
					} else {
						throw new Error("Invalid format");
					}
				} catch {
					const cellConfigFromStyles: BreakpointCellConfig = {
						backgroundColor: rgbToHex(attrs.backgroundColor || styles["background-color"] || ""),
						textColor: rgbToHex(attrs.textColor || styles.color || ""),
						textAlign: (attrs.textAlign || styles["text-align"] || "left") as "left" | "center" | "right",
						verticalAlign: (attrs.verticalAlign || styles["vertical-align"] || "middle") as "top" | "middle" | "bottom",
						padding: parsePaddingString(styles.padding || attrs.padding),
						fontSize: extractNumericValue(attrs.fontSize || styles["font-size"]),
						fontWeight: attrs.fontWeight || styles["font-weight"] || "",
						border: {
							top: {
								color: rgbToHex(styles["border-top-color"] || styles["border-color"] || ""),
								width: extractNumericValue(styles["border-top-width"] || styles["border-width"]) || "1",
							},
							right: {
								color: rgbToHex(styles["border-right-color"] || styles["border-color"] || ""),
								width: extractNumericValue(styles["border-right-width"] || styles["border-width"]) || "1",
							},
							bottom: {
								color: rgbToHex(styles["border-bottom-color"] || styles["border-color"] || ""),
								width: extractNumericValue(styles["border-bottom-width"] || styles["border-width"]) || "1",
							},
							left: {
								color: rgbToHex(styles["border-left-color"] || styles["border-color"] || ""),
								width: extractNumericValue(styles["border-left-width"] || styles["border-width"]) || "1",
							},
						},
					};

					setCellSettings({
						styles: {
							xs: { ...cellConfigFromStyles },
							sm: { ...cellConfigFromStyles },
							md: { ...cellConfigFromStyles },
							lg: { ...cellConfigFromStyles },
							xl: { ...cellConfigFromStyles },
						},
					});
				}
			} else {
				const cellConfigFromStyles: BreakpointCellConfig = {
					backgroundColor: rgbToHex(attrs.backgroundColor || styles["background-color"] || ""),
					textColor: rgbToHex(attrs.textColor || styles.color || ""),
					textAlign: (attrs.textAlign || styles["text-align"] || "left") as "left" | "center" | "right",
					verticalAlign: (attrs.verticalAlign || styles["vertical-align"] || "middle") as "top" | "middle" | "bottom",
					padding: parsePaddingString(styles.padding || attrs.padding),
					fontSize: extractNumericValue(attrs.fontSize || styles["font-size"]),
					fontWeight: attrs.fontWeight || styles["font-weight"] || "",
					border: {
						top: {
							color: rgbToHex(styles["border-top-color"] || styles["border-color"] || ""),
							width: extractNumericValue(styles["border-top-width"] || styles["border-width"]) || "1",
						},
						right: {
							color: rgbToHex(styles["border-right-color"] || styles["border-color"] || ""),
							width: extractNumericValue(styles["border-right-width"] || styles["border-width"]) || "1",
						},
						bottom: {
							color: rgbToHex(styles["border-bottom-color"] || styles["border-color"] || ""),
							width: extractNumericValue(styles["border-bottom-width"] || styles["border-width"]) || "1",
						},
						left: {
							color: rgbToHex(styles["border-left-color"] || styles["border-color"] || ""),
							width: extractNumericValue(styles["border-left-width"] || styles["border-width"]) || "1",
						},
					},
				};

				setCellSettings({
					styles: {
						xs: { ...cellConfigFromStyles },
						sm: { ...cellConfigFromStyles },
						md: { ...cellConfigFromStyles },
						lg: { ...cellConfigFromStyles },
						xl: { ...cellConfigFromStyles },
					},
				});
			}
		}

		// Automatically load cell settings from current cursor position when modal opens
		refreshCellSettings(editor);

		setShowUnifiedTableSettings(true);
	}, [allColumnSettings, allRowSettings, defaultRowConfig, refreshCellSettings]);

	// Method to close modal and prevent auto-reopen (used when "Apply" button is clicked)
	const closeUnifiedTableSettingsWithoutReopen = useCallback(() => {
		console.log("Setting shouldPreventAutoReopen flag to TRUE");
		shouldPreventAutoReopen.current = true;
		setShowUnifiedTableSettings(false);

		// Clear the flag after a short delay (enough time for any cursor restoration to complete)
		setTimeout(() => {
			console.log("Clearing shouldPreventAutoReopen flag to FALSE");
			shouldPreventAutoReopen.current = false;
		}, 200);
	}, []);

	return {
		tableRows, setTableRows,
		tableCols, setTableCols,
		tableWithHeader, setTableWithHeader,
		tableBorderColor, setTableBorderColor,
		tableBorderWidth, setTableBorderWidth,
		showTableModal, setShowTableModal,
		showUnifiedTableSettings, setShowUnifiedTableSettings,
		totalColumns, setTotalColumns,
		totalRows, setTotalRows,
		currentTableId,
		tableSettings, setTableSettings,
		columnSettings, setColumnSettings,
		allColumnSettings, setAllColumnSettings,
		rowSettings, setRowSettings,
		allRowSettings, setAllRowSettings,
		cellSettings, setCellSettings,
		selectedCellIndices,
		isInternalChange,
		shouldPreventAutoReopen,
		handleColumnSelect,
		handleRowSelect,
		openUnifiedTableSettings,
		refreshCellSettings,
		closeUnifiedTableSettingsWithoutReopen,
	};
};
